// utils/pwa-helpers.ts - PWA utility functions

import { useEffect } from 'react';

/**
 * Force refresh data when app becomes visible
 * Add this to your main components that load data
 */
export function useRefreshOnFocus(refreshCallback: () => void) {
  useEffect(() => {
    let isRefreshing = false;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isRefreshing) {
        isRefreshing = true;
        console.log('[PWA] App visible, refreshing data...');
        refreshCallback();
        setTimeout(() => {
          isRefreshing = false;
        }, 1000);
      }
    };

    const handleFocus = () => {
      if (!isRefreshing) {
        isRefreshing = true;
        console.log('[PWA] App focused, refreshing data...');
        refreshCallback();
        setTimeout(() => {
          isRefreshing = false;
        }, 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshCallback]);
}

/**
 * Clear all caches and reload
 * Useful for debugging or "force refresh" button
 */
export async function clearAllCachesAndReload() {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));
  }
  
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
  }
  
  setTimeout(() => {
    window.location.reload();
  }, 500);
}

/**
 * Check if running as installed PWA
 */
export function isPWA(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone ||
         document.referrer.includes('android-app://');
}

/**
 * Get cache version from service worker
 */
export async function getCacheVersion(): Promise<string | null> {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data.version || null);
      };
      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_VERSION' },
        [messageChannel.port2]
      );
      
      // Timeout after 2 seconds
      setTimeout(() => resolve(null), 2000);
    });
  }
  return null;
}

/**
 * Prevent white/black screen on iOS PWA
 * Add to index.html or App.tsx
 */
export function preventIOSBlankScreen() {
  // Prevent iOS blank screen on startup
  if (isPWA() && /iPhone|iPad|iPod/.test(navigator.userAgent)) {
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        console.log('[PWA] Page loaded from cache, forcing reload');
        window.location.reload();
      }
    });
  }
}